/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { type Href, Redirect, router, useLocalSearchParams } from "expo-router"
import { useEffect, useState } from "react"
import { jwtDecode } from "jwt-decode"
import { sdk } from "@/api/sdk"
import { GOOGLE_SCOPES } from "@/constants/Scopes"
import { useMutation } from "@tanstack/react-query"
import config from "@/config"
import { useRegistrationInputs } from "@/stores/register"
import { useGoogleLoginAuthMutation } from "@/queries/user"
import { type GoogleLoginAuthMutation } from "@/generated/graphql"
import { usePreQualState } from "@/stores/preQual"
import * as signInEvents from "@/analytics/events/sign-in"
import * as registerEvents from "@/analytics/events/register"
import { showErrorToast } from "@/components/toasts/AppToast"
import FullScreenLoader from "@/components/loading/FullScreenLoaderDark"

interface GoogleIdToken {
  iss: string
  azp: string
  aud: string
  sub: string
  email: string
  email_verified: boolean
  nbf: number
  name?: string
  picture?: string
  given_name?: string
  family_name?: string
  iat: number
  exp: number
  jti: string
}

export default function GoogleCallbackScreen() {
  const { code } = useLocalSearchParams<{
    code?: string
  }>()

  const [authState, setAuthState] = useState<
    "initial" | "verifying" | "failed" | "complete"
  >("initial")
  const [redirectTo, setRedirectTo] = useState<Href>("/login")

  const googleLoginAuthMutation = useGoogleLoginAuthMutation()
  const preQualState = usePreQualState()
  const isPreQualFlow = !!preQualState?.propertiesFlipped

  const {
    setFirstName,
    setLastName,
    setConfirmationMode,
    setGoogleCode,
    setEmail,
  } = useRegistrationInputs()

  const googleVerify = useMutation({
    // eslint-disable-next-line @typescript-eslint/unbound-method
    mutationFn: sdk.GoogleCodeVerify,
    onSuccess: (data) => {
      const existingUser = data.googleVerify?.existing || false
      const idToken = data.googleVerify?.idToken ?? null
      const userProfile = idToken ? jwtDecode<GoogleIdToken>(idToken) : null
      console.log({ existingUser, idToken, userProfile })
      if (existingUser && idToken) {
        googleLoginAuthMutation.mutate(
          {
            serviceType: "auth",
            idToken,
            scopes: GOOGLE_SCOPES,
            redirectUri: config.GOOGLE_REDIRECT_URI,
          },
          {
            onSuccess: (data: GoogleLoginAuthMutation) => {
              if (data?.googleLogin?.token) {
                signInEvents.googleSignInSubmitted({
                  email: userProfile?.email ?? "",
                })
                if (isPreQualFlow) {
                  setRedirectTo("/loans/")
                } else {
                  setRedirectTo("/")
                }
                setAuthState("complete")
              } else {
                showErrorToast({
                  title: "Sign In Error",
                  message: "Could not complete sign in.",
                })
                setRedirectTo("/login")
                setAuthState("failed")
              }
            },
            onError: (error) => {
              const errorResponse = (error as any)?.response
              if (errorResponse.errors) {
                let errorMessage = errorResponse.errors[0].message
                if (errorMessage === "GOOGLE_LOGIN_FAILED") {
                  errorMessage = "Google sign in failed."
                }

                if (errorMessage === "GOOGLE_NO_USER_FOUND") {
                  errorMessage = "User does not exist. Sign up instead."
                }

                showErrorToast({
                  title: "Sign In Error",
                  message: errorMessage,
                })
              }
              setRedirectTo("/login")
              setAuthState("failed")
              console.error(error)
            },
          }
        )
      } else if (idToken) {
        setFirstName(userProfile?.given_name ?? "")
        setLastName(userProfile?.family_name ?? "")
        setEmail(userProfile?.email ?? "")
        setGoogleCode({
          serverAuthCode: null,
          idToken,
          redirectUri: config.GOOGLE_REDIRECT_URI,
          scopes: GOOGLE_SCOPES,
          user: {
            id: userProfile?.sub ?? "",
            email: userProfile?.email ?? "",
            givenName: userProfile?.given_name ?? "",
            familyName: userProfile?.family_name ?? "",
            name: userProfile?.name ?? "",
            photo: userProfile?.picture ?? "",
          },
        })
        setConfirmationMode("google")
        registerEvents.googleAuthRegistrationSubmitted({
          email: userProfile?.email ?? "",
          userExists: false,
        })
        setRedirectTo("/confirm")
        setAuthState("complete")
      } else {
        showErrorToast({
          title: "Sign In Error",
          message: "Could not complete sign in.",
        })
        setRedirectTo("/register")
        setAuthState("failed")
      }
    },
  })

  useEffect(() => {
    if (code && authState === "initial") {
      setAuthState("verifying")
      googleVerify.mutate({
        code,
        redirectUri: config.GOOGLE_REDIRECT_URI,
        scopes: GOOGLE_SCOPES,
      })
    }
    if (!code) {
      setAuthState("failed")
    }
  }, [code])

  useEffect(() => {
    if (["complete", "failed"].includes(authState)) {
      console.log("!! Google redirecting to ", redirectTo)
      router.replace(redirectTo)
    }
  }, [authState])

  if (["initial", "verifying"].includes(authState)) {
    return <FullScreenLoader>Completing Sign In</FullScreenLoader>
  }

  if (["complete", "failed"].includes(authState)) {
    return <FullScreenLoader>Done</FullScreenLoader>
  }

  return <Redirect href={redirectTo} />
}
